import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { isNative } from "../lib/platform";
import { useToast } from "../lib/toast";
import { dismissBuild, dismissedBuild, installRelease, latestRelease, type Release } from "../lib/updates";
import { Icon } from "./Icon";

const EVERY_MS = 6 * 3600_000;

/**
 * ADR-0032: on the phone, a newer Omni shows as a banner above the tabs ("Later" hides it
 * for that version). A required version covers the screen until it is installed.
 */
export function UpdateBanner() {
  const nav = useNavigate();
  const toast = useToast();
  const [r, setR] = useState<Release | null>(null);
  const [pct, setPct] = useState<number | null>(null);

  useEffect(() => {
    if (!isNative) return;
    let last = 0;
    const check = () => {
      if (Date.now() - last < EVERY_MS) return;
      last = Date.now();
      latestRelease().then((x) => setR(x?.update ? x : null)).catch(() => undefined);
    };
    check();
    const onVisible = () => document.visibilityState === "visible" && check();
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  if (!r || (!r.required && r.build <= dismissedBuild())) return null;

  const install = async () => {
    setPct(0);
    try {
      await installRelease(r, setPct);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setPct(null);
    }
  };
  const firstLine = r.notes.split("\n").find((l) => l.trim())?.replace(/^[-*•]\s*/, "") ?? "";
  const action = (
    <button className="btn btn-primary btn-sm" disabled={pct !== null} onClick={install}>
      <Icon name="download" size={18} />{pct !== null ? `${pct}%` : "Update"}
    </button>
  );

  if (r.required) {
    return (
      <div className="update-required" role="alertdialog" aria-labelledby="upd-title">
        <div className="stack">
          <span className="update-ic lg"><Icon name="download" size={28} /></span>
          <h1 id="upd-title">Update Omni</h1>
          <p className="muted">This version of the app is no longer supported. Install Omni {r.version} to carry on.</p>
          {r.notes && <p className="update-notes">{r.notes}</p>}
          <button className="btn btn-primary btn-block" disabled={pct !== null} onClick={install}>
            <Icon name="download" size={20} />{pct !== null ? `Downloading… ${pct}%` : `Install ${r.version}`}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="update-bar" role="status">
      <button className="update-body" onClick={() => nav("/settings/app")}>
        <span className="update-ic"><Icon name="download" size={18} /></span>
        <span className="notice-text">
          <strong>Omni {r.version} is available</strong>
          {firstLine && <span className="ellipsis">{firstLine}</span>}
        </span>
      </button>
      {action}
      <button className="notice-close" aria-label="Later" onClick={() => { dismissBuild(r.build); setR(null); }}><Icon name="close" size={16} /></button>
    </div>
  );
}
